import React, { useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Badge,
  ButtonBase,
} from "@mui/material";
import { Phone, Plus, ChevronRight, Check } from "lucide-react";
import { motion } from "framer-motion";

import { PackageModel } from "../repositories/package.repository";
import { useCurrencyStore } from "../store/useCurrencyStore";
import { formatPackagePrice } from "../utils/currency";
import { gradients } from "../styles/theme";

interface CardPackageProps {
  package: PackageModel;
  onSelect: (pkg: PackageModel) => void;
  onAddToCart: (pkg: PackageModel) => void;
  numSelected?: number;
}

const MAX_VISIBLE_COUNTRIES = 4;

const CardPackage: React.FC<CardPackageProps> = ({
  package: pkg,
  onSelect,
  onAddToCart,
  numSelected = 0,
}) => {
  const isSelected = numSelected > 0;
  const [expanded, setExpanded] = useState(false);
  const currency = useCurrencyStore((s) => s.currency);

  const visibleCountries = useMemo(() => {
    return expanded
      ? pkg.countries
      : pkg.countries.slice(0, MAX_VISIBLE_COUNTRIES);
  }, [expanded, pkg.countries]);

  const hiddenCount = Math.max(0, pkg.countries.length - MAX_VISIBLE_COUNTRIES);
  const hasCountries = pkg.countries.length > 0;

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <Box
      component={motion.div}
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.99 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "flex",
      }}
    >
      {/* Floating add-to-cart button */}
      <Tooltip
        title={isSelected ? "Add another" : "Add to cart"}
        placement="left"
      >
        <Badge
          badgeContent={numSelected}
          color="primary"
          overlap="circular"
          sx={{
            position: "absolute",
            top: 14,
            right: 14,
            zIndex: 1,
            "& .MuiBadge-badge": {
              fontWeight: 700,
              fontSize: 11,
              minWidth: 18,
              height: 18,
              border: "2px solid #FFFFFF",
            },
          }}
        >
          <IconButton
            onClick={(e) => {
              stop(e);
              onAddToCart(pkg);
            }}
            aria-label="Add to cart"
            sx={{
              width: 42,
              height: 42,
              background: gradients.hero,
              color: "#FFFFFF",
              boxShadow: "0 6px 16px rgba(232,0,13,0.32)",
              transition: "all 0.18s ease",
              "&:hover": {
                background: gradients.hero,
                transform: "scale(1.06)",
                boxShadow: "0 10px 22px rgba(232,0,13,0.38)",
              },
            }}
          >
            {isSelected ? (
              <Check size={18} strokeWidth={2.5} />
            ) : (
              <Plus size={20} strokeWidth={2.5} />
            )}
          </IconButton>
        </Badge>
      </Tooltip>

      <ButtonBase
        onClick={() => onSelect(pkg)}
        focusRipple
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "stretch",
          borderRadius: "20px",
          textAlign: "left",
        }}
      >
        <Stack
          spacing={1.25}
          sx={{
            width: "100%",
            height: "100%",
            minHeight: 280,
            boxSizing: "border-box",
            borderRadius: "20px",
            p: { xs: 1.75, sm: 2 },
            pr: { xs: 1.75, sm: 2.5 },
            background: isSelected ? gradients.card : "#FFFFFF",
            border: "1px solid",
            borderColor: isSelected ? "primary.main" : "divider",
            boxShadow: "0 2px 10px rgba(15,15,15,0.05)",
            transition: "all 0.25s ease",
            overflow: "hidden",
            "&:hover": {
              boxShadow: "0 14px 32px rgba(15,15,15,0.08)",
            },
          }}
        >
          {/* Header */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ pr: 5 }}>
            <Chip
              label={pkg.area}
              size="small"
              sx={{
                bgcolor: "#FFF5F5",
                color: "primary.main",
                fontWeight: 700,
                letterSpacing: "0.03em",
                height: 24,
                maxWidth: "100%",
              }}
            />

            {hasCountries && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {pkg.countries.length}{" "}
                {pkg.countries.length === 1 ? "country" : "countries"}
              </Typography>
            )}
          </Stack>

          {/* Name */}
          <Typography
            variant="body1"
            sx={{
              fontSize: "1.05rem",
              fontWeight: 700,
              lineHeight: 1.35,
              pr: 5,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              minHeight: 48,
            }}
          >
            {pkg.packageName}
          </Typography>

          {/* Countries */}
          {hasCountries && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.75,
                alignContent: "flex-start",
                minHeight: 52,
              }}
            >
              {visibleCountries.map((country) => (
                <Chip
                  key={country.countryId}
                  label={country.countryName}
                  size="small"
                  sx={{
                    maxWidth: "100%",
                    height: 24,
                    bgcolor: "rgba(15,15,15,0.04)",
                    color: "text.primary",
                    fontWeight: 500,
                    fontSize: "0.72rem",
                    "& .MuiChip-label": {
                      px: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              ))}

              {!expanded && hiddenCount > 0 && (
                <Chip
                  label={`+${hiddenCount}`}
                  size="small"
                  clickable
                  onClick={(e) => {
                    stop(e);
                    setExpanded(true);
                  }}
                  sx={{
                    height: 24,
                    bgcolor: "rgba(232,0,13,0.08)",
                    color: "primary.main",
                    fontWeight: 700,
                    fontSize: "0.72rem",
                  }}
                />
              )}
            </Box>
          )}

          {/* Minutes */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{ color: "text.secondary" }}
          >
            <Phone size={14} />

            <Typography
              variant="caption"
              sx={{
                fontWeight: 500,
                lineHeight: 1,
              }}
            >
              {pkg.minutes}
            </Typography>
          </Stack>

          {/* Push footer to bottom */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Footer */}
          <Stack
            direction="row"
            alignItems="flex-end"
            justifyContent="space-between"
            sx={{
              pt: 1.25,
              mt: "auto",
              borderTop: "1px solid",
              borderColor: "divider",
              gap: 1,
            }}
          >
            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontWeight: 500,
                  lineHeight: 1,
                }}
              >
                From
              </Typography>

              <Typography
                color="primary.main"
                sx={{
                  fontSize: "1.45rem",
                  fontWeight: 800,
                  lineHeight: 1.1,
                  wordBreak: "break-word",
                }}
              >
                {formatPackagePrice(pkg, currency)}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              spacing={0.35}
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontSize: 13,
                flexShrink: 0,
              }}
            >
              <span>View</span>
              <ChevronRight size={16} />
            </Stack>
          </Stack>
        </Stack>
      </ButtonBase>
    </Box>
  );
};

export default CardPackage;
